import { useQuery } from 'react-query'

import { getLiveId } from '../../global/blocs/roomInfo'
import { getAudienceList } from '../api/api'
import { type AudienceMember, mapDTOToAudienceMember } from '../model/Audience'
import queryClient from '../../queryClient'
import { subscribe } from '../../realtime/audienceRealtime'

const AUDIENCE_QUERY_KEY = 'audience'

const audienceQueryKeys = {
  all: [AUDIENCE_QUERY_KEY],
  list: [AUDIENCE_QUERY_KEY, 'list'],
  get: (uuid: string) => [AUDIENCE_QUERY_KEY, uuid],
}

let subscribed = false
const subscribeAudienceRealtimeUpdates = () => {
  if (subscribed) return

  subscribed = true
  subscribe((audienceMemberDTO) => {
    const audienceMember = mapDTOToAudienceMember(audienceMemberDTO)
    queryClient.setQueryData(
      audienceQueryKeys.get(audienceMember.uuid),
      audienceMember
    )
  }).catch(() => {
    subscribed = false
  })
}

const useAudienceMemberListQuery = () => {
  return useQuery({
    queryKey: audienceQueryKeys.list,
    queryFn: async () => {
      const liveId = getLiveId()
      subscribeAudienceRealtimeUpdates()

      const response = await getAudienceList({
        includeTimelines: true,
        includeEvents: false,
        timelineObjects: [liveId],
        objects: [liveId],
        orderBy: 'recent',
        order: 'desc',
        size: 200,
      })

      const audience = response.data.map(mapDTOToAudienceMember)
      audience.forEach((a) => {
        queryClient.setQueryData(audienceQueryKeys.get(a.uuid), a)
      })

      return audience
        .filter(
          (a) =>
            a.timelines?.find((timeline) => +timeline.object === liveId)?.active
        )
        .map((a) => a.uuid)
    },
    refetchInterval: 10e3,
    staleTime: Infinity,
    cacheTime: Infinity,
  })
}

const useAudienceMemberQuery = (uuid: string) => {
  return useQuery<AudienceMember>({
    queryKey: audienceQueryKeys.get(uuid),
  })
}

export { useAudienceMemberQuery, useAudienceMemberListQuery }
